import { useQuery } from '@tanstack/react-query'
import api from '../../../api'
import { cn } from '../../../lib/utils'
import StatusBadge from '../../ui/StatusBadge'
import { AiInsightCard } from '../../finance/AiInsightCard'
import { DetailSkeleton } from '../../finance/SkeletonStates'
import { formatCurrency } from '../../../config/chartTheme'
import { Shield, ChevronRight } from 'lucide-react'

interface Level5ExceptionProps {
  exceptionId: number
  onDrillResolution: () => void
}

export function Level5Exception({ exceptionId, onDrillResolution }: Level5ExceptionProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['exception-detail', exceptionId],
    queryFn: () => api.get(`/exceptions/${exceptionId}`).then(r => r.data),
  })

  if (isLoading) return <DetailSkeleton />

  const exc = data || {}
  const result = exc.resolution || exc.result || exc.exception_result || {}

  return (
    <div className="space-y-4">
      {/* Exception Header */}
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-5',
        'border-l-[3px]',
        exc.severity === 'CRITICAL' ? 'border-l-red-500'
          : exc.severity === 'HIGH' ? 'border-l-amber-500'
          : 'border-l-cyan-500',
      )}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-['JetBrains_Mono',monospace] text-[11px] text-[var(--text-muted,#64748B)]">
                #{exceptionId}
              </span>
              <StatusBadge status={exc.status || 'PENDING'} />
              {exc.severity && <StatusBadge status={exc.severity} />}
            </div>
            <h3 className="text-[15px] font-medium text-[var(--text-primary,#F0F2F5)]">
              {exc.title || exc.exception_type || 'Exception'}
            </h3>
          </div>
          <div className="text-right">
            <div className="font-['JetBrains_Mono',monospace] text-[16px] font-semibold text-[var(--text-primary,#F0F2F5)]">
              {formatCurrency(exc.amount)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {[
            { label: 'Type', value: exc.exception_type },
            { label: 'Department', value: exc.department },
            { label: 'Entity', value: exc.entity_code },
            { label: 'Detected', value: exc.detected_at || exc.created_at },
          ].map((item, i) => (
            <div key={i}>
              <div className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-0.5">
                {item.label}
              </div>
              <div className="text-[12px] text-[var(--text-secondary,#94A3B8)]">
                {item.value || '\u2014'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Diagnosis */}
      {(result.recommendation_summary || result.root_cause || exc.description) && (
        <AiInsightCard
          title="AI Diagnosis"
          description={result.recommendation_summary || result.root_cause || exc.description || ''}
          confidence={result.confidence_score}
        />
      )}

      {/* Resolution Link */}
      <button
        onClick={onDrillResolution}
        className={cn(
          'w-full flex items-center justify-between rounded-lg p-4',
          'border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
          'hover:bg-white/[0.04] transition-colors',
        )}
      >
        <div className="flex items-center gap-3">
          <Shield className="h-4 w-4 text-[var(--accent,var(--accent))]" />
          <span className="text-[13px] text-[var(--text-secondary,#94A3B8)]">
            View Resolution Detail
          </span>
        </div>
        <ChevronRight className="h-4 w-4 text-[var(--text-hint,#475569)]" />
      </button>
    </div>
  )
}
